import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export interface CertificationListItem {
  id: number;
  name: string;
  authority: string | null;
  external_ref: string | null;
  issuing_company: { id: number; name: string; external_ref: string | null } | null;
  candidate_count: number;
}

export interface PaginatedCertificationsResponse {
  items: CertificationListItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export async function listCertifications(
  navigate: NavigateFunction,
  params?: { page?: number; size?: number; sort_by?: string; sort_order?: string; q?: string }
): Promise<PaginatedCertificationsResponse> {
  const q = new URLSearchParams();
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 25));
  if (params?.sort_by) q.set("sort_by", params.sort_by);
  if (params?.sort_order) q.set("sort_order", params.sort_order);
  if (params?.q) q.set("q", params.q);
  const url = `${API_URL}/certifications?${q.toString()}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des certifications");
  return res.json();
}

export interface ClusterMember {
  id: number;
  name: string;
  authority: string | null;
  issuing_company_id: number | null;
  candidate_count: number;
  code: string | null;
}

export interface DuplicateCluster {
  confidence: "catalog-specific" | "catalog-generic" | "name";
  is_generic: boolean;
  auto_mergeable: boolean;
  canonical_name: string | null;
  suggested_target_id: number;
  member_count: number;
  total_candidates: number;
  members: ClusterMember[];
}

export interface DuplicatesResponse {
  clusters: DuplicateCluster[];
  cluster_count: number;
  mergeable_rows: number;
  candidates_affected: number;
}

export async function listDuplicateClusters(
  navigate: NavigateFunction,
  params?: { min_candidates?: number; limit?: number }
): Promise<DuplicatesResponse> {
  const q = new URLSearchParams();
  q.set("min_candidates", String(params?.min_candidates ?? 2));
  q.set("limit", String(params?.limit ?? 200));
  const url = `${API_URL}/certifications/duplicates?${q.toString()}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la détection des doublons");
  return res.json();
}

export interface MergeResult {
  merged: boolean;
  target?: { id: number; name: string };
  merged_count?: number;
  reassigned_links?: number;
  warnings?: string[];
  message?: string;
}

export async function mergeCertifications(
  navigate: NavigateFunction,
  body: { target_id: number; source_ids: number[]; force?: boolean }
): Promise<MergeResult> {
  const url = `${API_URL}/certifications/merge`;
  const res = await apiFetch(
    url,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors de la fusion");
  return res.json();
}
